import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronUp, PlusCircle, RotateCcw } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import Brand from "../../core/modals/inventory/brand";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { all_routes } from "../../Router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Datatable from "../../core/pagination/datatable";
import { setToogleHeader } from "../../core/redux/action";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";

const ProductList = () => {

  const { showToast } = useToast();
  const dataSource = useSelector((state) => state.product_list);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalCount: 0,
  });
  const isDeleting = useRef(false);
  const hasFetched = useRef(false);
  const operationResult = useRef(null);
  const route = all_routes;
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    let parsedPermissions = [];
      try {
        const Permissions = JSON.parse(storedPermissions);
        const getPermissions = JSON.parse(Permissions.permissions)
        if(getPermissions.permissions){
          parsedPermissions = getPermissions.permissions;
        }
        else {
          parsedPermissions = getPermissions;
        }
        
        const inventoryPermissions = parsedPermissions.find(
          (item) => item.title === "Inventory"
        );

        const productPermissions = inventoryPermissions?.childPage?.find(
          (child) => child.title === "Products"
        );
        // console.log("search product", productPermissions)

        if (productPermissions) {
          setPermissions({
            create: productPermissions.create || false,
            read: productPermissions.read || false,
            update: productPermissions.update || false,
            delete: productPermissions.delete || false,
          });
        } else {
          console.error("Product permissions not found in localStorage");
          showToast("Product permissions not found.", "error", "Permissions Error");
        }
      } catch (error) {
        console.error("Error parsing permissions from localStorage:", error);
        showToast("Failed to load permissions.", "error", "Permissions Error");
      }
  }, []);

  const fetchProducts = async (currentPage = 1, pageSize = 5) => {
    if (isLoading || isDeleting.current) {
      return;
    }

    setIsLoading(true);
    try {
      const pageIndex = currentPage - 1;
      const response = await apiClient.post('/get-all-products', {
        pageIndex,
        pageSize,
      });

      if (!response?.data || !Array.isArray(response.data)) {
        throw new Error("Invalid data from server");
      }

      const requiredFields = ["id", "name", "sku", "product_type", "category_name"];
      const formattedProducts = response.data.map((product) => {
        const missing = requiredFields.filter(field => !Object.prototype.hasOwnProperty.call(product, field));
        if (missing.length) return null;
        let imageUrl = "assets/img/product/noimage.png";
        if (product.image) {
          try {
            const images = Array.isArray(product.image) ? product.image : JSON.parse(product.image);
            if (images.length) imageUrl = images[0];
          } catch (err) {
            console.error("Error parsing product image:", err);
            showToast("Invalid product image format.", "error", "Product Error");
            return null;
          }
        }

        let price = product.price;
        let qty = product.quantity ?? 0;
        if (product.product_type === "variable" && Array.isArray(product.variants)) {
          price = product.variants.map((v) => parseFloat(v.price));
          qty = product.variants.map((v) => v.quantity ?? 0);
        }

        return {
          product: product.name,
          sku: product.sku,
          category: product.category_name || "Unknown",
          brand: product.branch_name || "Unknown",
          price,
          productType: product.product_type,
          qty,
          productImage: imageUrl,
          id: product.id,
        };
      }).filter(Boolean);

      dispatch({ type: "Product_list", payload: formattedProducts });

      setPagination({
        currentPage: response.currentPage, 
        pageSize: response.pageSize ?? pageSize,
        totalPages: response.totalPages ?? 1,
        totalCount: response.totalCount ?? 0,
      });
    } catch (err) {
      showToast(err.message || "Failed to fetch products.", "error", "Product Error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchProducts(pagination.currentPage, pagination.pageSize);
      hasFetched.current = true;
    }
  }, [pagination.currentPage, pagination.pageSize]);

  const handleDelete = async (productId) => {
    const MySwal = withReactContent(Swal);
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          isDeleting.current = true;
          const response = await apiClient.delete(`/delete-product/${productId}`);
          if (response?.success) {
            showToast("Product deleted successfully.", "success", "Product");
            MySwal.fire({
              title: "Deleted!",
              text: "Product deleted successfully.",
              confirmButtonText: "OK",
              customClass: { confirmButton: "btn btn-success" },
            });

            hasFetched.current = false;
          } else {
            throw new Error(response.message || "Unexpected response from server.");
          }
        } catch (error) {
          console.error("Error deleting product:", error.message);
          showToast(error.message || "Failed to delete product.", "error", "Product Error");
          MySwal.fire({
            title: "Error!",
            text: error.message || "Failed to delete product.",
            confirmButtonText: "OK",
            customClass: { confirmButton: "btn btn-danger" },
          });
        } finally {
          setIsLoading(false);
          isDeleting.current = false;
          await fetchProducts(pagination.currentPage, pagination.pageSize);
        }
      }
    });
  };

  const handleModalClose = () => {
    if (operationResult.current?.success) {
      hasFetched.current = false;
      fetchProducts(pagination.currentPage, pagination.pageSize);
    }
    operationResult.current = null;
  };

  const handleOperationComplete = (result) => {
    operationResult.current = result;
  };

  const columns = [
    {
      title: "Product",
      dataIndex: "product",
      render: (text, record) => (
        <span className="productimgname">
          <Link className="product-img stock-img">
            <ImageWithBasePath alt="Product" src={record.productImage || "assets/img/default-product.jpg"} />
          </Link>
          <Link>{text}</Link>
        </span>
      ),
      sorter: (a, b) => a.product.length - b.product.length,
    },
    {
      title: "SKU",
      dataIndex: "sku",
      sorter: (a, b) => a.sku.length - b.sku.length,
    },
    {
      title: "Category",
      dataIndex: "category",
      sorter: (a, b) => a.category.length - b.category.length,
    },
    {
      title: "Branch",
      dataIndex: "brand",
      sorter: (a, b) => a.brand.length - b.brand.length,
    },
    {
      title: "Price",
      dataIndex: "price",
      render: (value) =>
        Array.isArray(value) ? `[${value.join(", ")}]` : `$${value}`,
      sorter: (a, b) => {
        const getPrice = (val) =>
          Array.isArray(val) ? Math.min(...val) : parseFloat(val);
        return getPrice(a.price) - getPrice(b.price);
      },
    },
    {
      title: "Product Type",
      dataIndex: "productType",
      sorter: (a, b) => a.productType.length - b.productType.length,
    },
    {
      title: "Qty",
      dataIndex: "qty",
      render: (value) =>
        Array.isArray(value) ? `[${value.join(", ")}]` : value,
      sorter: (a, b) => {
        const getQty = (val) =>
          Array.isArray(val) ? val.reduce((acc, v) => acc + v, 0) : val;
        return getQty(a.qty) - getQty(b.qty);
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: 'actions',
      render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            {(permissions.read) && (
              <Link className="me-2 p-2" to={`${route.editproduct}/${record.id}`}>
                <i className="feather-edit" data-feather="edit"></i>
              </Link>
            )}
            {(permissions.delete) && (
              <Link
              className="confirm-text p-2"
              to="#"
              onClick={() => handleDelete(record.id)}
            >
              <i className="feather-trash-2" data-feather="trash-2"></i>
            </Link>
            )}
          </div>
        </td>
      ),
    },
  ];

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Product List</h4>
              <h6>Manage your products</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" onClick={() => {
                  hasFetched.current = false;
                  fetchProducts(pagination.currentPage, pagination.pageSize);
                }}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            {permissions.create && (
              <Link to={route.addproduct} className="btn btn-added">
                <PlusCircle className="me-2 iconsize" />
                Add New Product
              </Link>
            )}
            {/* <Link to={route.addproduct} className="btn btn-added">
              <PlusCircle className="me-2 iconsize" />
              Add New Product
            </Link> */}
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                    aria-label="Search products"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <Datatable
                columns={columns}
                dataSource={dataSource}
                rowSelection={null}
                pagination={{
                  current: pagination.currentPage,
                  pageSize: pagination.pageSize,
                  total: pagination.totalCount,
                  onChange: async (page, pageSize) => {
                    // Update state and trigger API call
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: page,
                      pageSize: pageSize,
                    }));
                    await fetchProducts(page, pageSize); // Fetch data for the new page
                  },
                  showSizeChanger: true, // Allows changing page size
                }}
              />
            </div>
          </div>
        </div>
        <Brand onOperationComplete={handleOperationComplete} onClose={handleModalClose} />
      </div>
    </div>
  );
};

export default ProductList;